import React, { useEffect, useMemo } from 'react';
import thaiAddressData from '@/data/thai-address.json';

interface Subdistrict {
  subdistrict: string;
  zipcode: string;
}

interface District {
  district: string;
  subdistricts: Subdistrict[];
}

interface Province {
  province: string;
  districts: District[];
}

interface ThaiAddressSelectorProps {
  province?: string;
  district?: string;
  subdistrict?: string;
  postalCode?: string;
  onProvinceChange: (value: string) => void;
  onDistrictChange: (value: string) => void;
  onSubdistrictChange: (value: string) => void;
  onPostalCodeChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
}

export default function ThaiAddressSelector({
  province = '',
  district = '',
  subdistrict = '',
  postalCode = '',
  onProvinceChange,
  onDistrictChange,
  onSubdistrictChange,
  onPostalCodeChange,
  required = false,
  disabled = false,
}: ThaiAddressSelectorProps) {
  const addressData = thaiAddressData as Province[];

  // Get available districts based on selected province
  const availableDistricts = useMemo(() => {
    if (!province) return [];
    const selectedProvince = addressData.find((p) => p.province === province);
    return selectedProvince?.districts || [];
  }, [province, addressData]);

  // Get available subdistricts based on selected district
  const availableSubdistricts = useMemo(() => {
    if (!district || availableDistricts.length === 0) return [];
    const selectedDistrict = availableDistricts.find((d) => d.district === district);
    return selectedDistrict?.subdistricts || [];
  }, [district, availableDistricts]);

  // Auto-fill postal code when subdistrict is selected
  useEffect(() => {
    if (subdistrict && availableSubdistricts.length > 0) {
      const selectedSubdistrict = availableSubdistricts.find(
        (s) => s.subdistrict === subdistrict
      );
      if (selectedSubdistrict) {
        onPostalCodeChange(selectedSubdistrict.zipcode);
      }
    }
  }, [subdistrict, availableSubdistricts]);

  const handleProvinceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onProvinceChange(value);
    // Reset dependent fields
    onDistrictChange('');
    onSubdistrictChange('');
    onPostalCodeChange('');
  };

  const handleDistrictChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onDistrictChange(value);
    // Reset dependent fields
    onSubdistrictChange('');
    onPostalCodeChange('');
  };

  const handleSubdistrictChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onSubdistrictChange(value);
  };

  return (
    <>
      {/* จังหวัด */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          จังหวัด {required && <span className="text-red-500">*</span>}
        </label>
        <select
          value={province}
          onChange={handleProvinceChange}
          required={required}
          disabled={disabled}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
        >
          <option value="">-- เลือกจังหวัด --</option>
          {addressData.map((p) => (
            <option key={p.province} value={p.province}>
              {p.province}
            </option>
          ))}
        </select>
      </div>

      {/* อำเภอ */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          อำเภอ/เขต {required && <span className="text-red-500">*</span>}
        </label>
        <select
          value={district}
          onChange={handleDistrictChange}
          required={required}
          disabled={disabled || !province}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
        >
          <option value="">-- เลือกอำเภอ/เขต --</option>
          {availableDistricts.map((d) => (
            <option key={d.district} value={d.district}>
              {d.district}
            </option>
          ))}
        </select>
      </div>

      {/* ตำบล */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          ตำบล/แขวง {required && <span className="text-red-500">*</span>}
        </label>
        <select
          value={subdistrict}
          onChange={handleSubdistrictChange}
          required={required}
          disabled={disabled || !district}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
        >
          <option value="">-- เลือกตำบล/แขวง --</option>
          {availableSubdistricts.map((s) => (
            <option key={s.subdistrict} value={s.subdistrict}>
              {s.subdistrict}
            </option>
          ))}
        </select>
      </div>

      {/* รหัสไปรษณีย์ */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          รหัสไปรษณีย์ {required && <span className="text-red-500">*</span>}
        </label>
        <input
          type="text"
          value={postalCode}
          onChange={(e) => onPostalCodeChange(e.target.value)}
          required={required}
          disabled={disabled}
          maxLength={5}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
          placeholder="50000"
        />
        {subdistrict && postalCode && (
          <p className="text-xs text-gray-500 mt-1">
            รหัสไปรษณีย์ถูกกรอกอัตโนมัติตามตำบลที่เลือก
          </p>
        )}
      </div>
    </>
  );
}
